import React from 'react';
import { FormattedMessage } from 'react-intl';

import { DialogContentText } from '@mui/material';

import type { Dictionary } from '../../utils';
import { isObject, makeButtonCancelTestId, makeButtonConfirmTestId } from '../../utils';

import format from '../../utils/formatter';

import { TranslationKeys } from '../../translations';

import ActionButton from '../ActionButton';
import { Dialog } from '../Dialog';

import { DivPanelsContainerStyled } from './SubmitConfirmationDialog.styles';
import { SubmitConfirmationDialogDataPanel } from './SubmitConfirmationDialogDataPanel';

import type {
  ChangedValues,
  CheckValuesRecursivelyFunc,
  GetInputOptionsAndLabelFunc,
  GetSelectOrAutocompleteDisplayValueFunc,
} from './SubmitConfirmationDialog.types';

type SubmitConfirmationDialogProps = {
  open: boolean;
  onConfirm: () => void;
  onClose: () => void;
  previousValues: Dictionary<any>;
  newValues: Dictionary<any>;
  getInputOptionsAndLabel: GetInputOptionsAndLabelFunc;
  getSelectOrAutocompleteDisplayValue?: GetSelectOrAutocompleteDisplayValueFunc;
  testId?: string;
};

export const SubmitConfirmationDialog = ({
  open,
  onConfirm,
  onClose,
  previousValues,
  newValues,
  getInputOptionsAndLabel,
  getSelectOrAutocompleteDisplayValue,
  testId,
}: SubmitConfirmationDialogProps) => {
  const setFieldData = React.useCallback(
    ({ inputName, value }) => {
      const input = getInputOptionsAndLabel(inputName);

      // Add safety check using optional chaining
      if (!input?.options) {
        return {
          label: inputName,
          options: { style: 'text' },
          value: Array.isArray(value) ? value.join(', ') : value,
        };
      }

      const isSelectOrAutocomplete = ['select', 'autocomplete', 'multi-select'].includes(input.options?.style || '');
      const isSelectOrAutoCompleteAndGetterFunctionWasProvided =
        isSelectOrAutocomplete && getSelectOrAutocompleteDisplayValue;

      let inputValue = isSelectOrAutoCompleteAndGetterFunctionWasProvided
        ? getSelectOrAutocompleteDisplayValue({ inputName, inputValue: value })
        : value;

      if (input.options?.style === 'date') {
        inputValue = format.date(value);
      }

      // Handle arrays (like multiselect values) by joining them
      if (Array.isArray(inputValue) && typeof inputValue[0] === 'string') {
        inputValue = inputValue.join(', ');
      }

      return {
        label: input.label,
        options: input.options,
        value: inputValue,
      };
    },
    [getInputOptionsAndLabel, getSelectOrAutocompleteDisplayValue],
  );

  const checkValuesRecursively = React.useCallback<CheckValuesRecursivelyFunc>(
    ({ inputName, previousValue, newValue }): ChangedValues | undefined => {
      // Handle arrays (like multiselect values)
      if (Array.isArray(previousValue) || Array.isArray(newValue)) {
        const prevArray = Array.isArray(previousValue) ? previousValue : [];
        const newArray = Array.isArray(newValue) ? newValue : [];

        // Compare arrays by converting to strings for simple comparison
        // Use slice().sort() to avoid mutating original arrays and provide proper compare function
        const sortedPrevArray = prevArray.slice().sort((a, b) => String(a).localeCompare(String(b)));
        const sortedNewArray = newArray.slice().sort((a, b) => String(a).localeCompare(String(b)));

        if (JSON.stringify(sortedPrevArray) !== JSON.stringify(sortedNewArray)) {
          return {
            previousValues: {
              [inputName]: setFieldData({ inputName, value: previousValue }),
            },
            newValues: {
              [inputName]: setFieldData({ inputName, value: newValue }),
            },
          };
        }
        return undefined;
      }

      // Handle primitive values (string, number, boolean, null, undefined)
      if (!isObject(previousValue) && !isObject(newValue)) {
        if (previousValue !== newValue) {
          return {
            previousValues: {
              [inputName]: setFieldData({ inputName, value: previousValue }),
            },
            newValues: {
              [inputName]: setFieldData({ inputName, value: newValue }),
            },
          };
        }
        return undefined;
      }

      // Handle objects
      if (isObject(previousValue) && isObject(newValue)) {
        return Object.keys(previousValue).reduce<ChangedValues>(
          (acc, key) => {
            const ret = checkValuesRecursively({
              inputName: key,
              previousValue: previousValue[key],
              newValue: newValue[key],
            });

            if (!ret) {
              return acc;
            }

            acc.previousValues = {
              ...acc?.previousValues,
              ...(ret?.previousValues || {}),
            };
            acc.newValues = {
              ...acc?.newValues,
              ...(ret?.newValues || {}),
            };
            return acc;
          },
          { previousValues: {}, newValues: {} },
        );
      }

      return undefined;
    },
    [setFieldData],
  );

  const changedValues = React.useMemo(() => {
    const hasChanges = JSON.stringify(previousValues) !== JSON.stringify(newValues);

    if (!hasChanges) {
      return null;
    }

    return Object.keys(previousValues).reduce<ChangedValues>(
      (acc, key) => {
        const ret = checkValuesRecursively({
          inputName: key,
          previousValue: previousValues[key],
          newValue: newValues[key],
        });

        if (!ret) {
          return acc;
        }

        acc.previousValues = {
          ...acc.previousValues,
          ...(ret?.previousValues || {}),
        };
        acc.newValues = {
          ...acc.newValues,
          ...(ret?.newValues || {}),
        };

        return acc;
      },
      { previousValues: {}, newValues: {} },
    );
  }, [previousValues, newValues, checkValuesRecursively]);

  const DialogActions = React.useMemo(() => {
    return (
      <>
        <ActionButton
          messageId={TranslationKeys.button_cancel}
          onClick={onClose}
          variant="outlined"
          testId={makeButtonCancelTestId(testId)}
        />
        <ActionButton
          messageId={TranslationKeys.button_save}
          onClick={onConfirm}
          testId={makeButtonConfirmTestId(testId)}
        />
      </>
    );
  }, [onClose, onConfirm, testId]);

  return (
    <Dialog
      open={open}
      maxWidth={'md'}
      title={<FormattedMessage id={TranslationKeys.global_saveChanges} />}
      actions={DialogActions}
      testId={testId}
    >
      <DialogContentText>
        <FormattedMessage id={TranslationKeys.global_confirmSaveChanges} />
      </DialogContentText>

      <DivPanelsContainerStyled>
        <SubmitConfirmationDialogDataPanel
          title={TranslationKeys.global_originalData}
          values={changedValues?.previousValues}
        />
        <SubmitConfirmationDialogDataPanel
          title={TranslationKeys.global_newData}
          values={changedValues?.newValues}
          highlighted
        />
      </DivPanelsContainerStyled>
    </Dialog>
  );
};
